import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchableSelect from '@/components/SearchableSelect';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, People as PeopleIcon } from '@mui/icons-material';
import { tradersService, dictionariesService, usersService } from '../../../services';
import { TraderBalance, TraderRate, Trader, TraderRateAgent } from '../../../types/trader';
import { User } from '../../../types/auth';
import { RoleEnum } from '../../../types/auth/roles';
import { PaymentSystem } from '../../../types/common/payment';
import AnimatedPage from '../../../components/AnimatedPage';
import { useNotification } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';

const INSURANCE_BALANCE_ERROR = 'Страховой баланс трейдера должен быть больше 0';

type ApiError = {
  response?: { data?: { detail?: string } };
  message?: string;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null) return fallback;
  const apiError = error as ApiError;
  return apiError.response?.data?.detail || apiError.message || fallback;
};

interface EditRateDialogProps {
  open: boolean;
  rate: TraderRate | null;
  onClose: () => void;
  onSave: (rateId: string, data: Partial<TraderRate>) => Promise<void>;
  paymentSystems: PaymentSystem[];
  canActivate: (traderId?: string, paymentSystemId?: string) => boolean;
}

const EditRateDialog: React.FC<EditRateDialogProps> = ({ open, rate, onClose, onSave, paymentSystems, canActivate }) => {
  const [formData, setFormData] = useState<Partial<TraderRate>>({
    rate: 0,
    priority: 0,
    is_active: true,
    direction: 'IN' as const,
    additional_commission: 0,
    referral_percent: 0,
    payment_system_id: '',
  });

  useEffect(() => {
    if (rate) {
      setFormData({
        rate: rate.rate,
        priority: rate.priority,
        is_active: rate.is_active,
        direction: rate.direction,
        additional_commission: rate.additional_commission,
        referral_percent: rate.referral_percent || 0,
        payment_system_id: rate.payment_system_id,
      });
    }
  }, [rate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rate) {
      await onSave(rate.id, formData);
      onClose();
    }
  };

  const handleChange = (field: keyof TraderRate) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: 'direction' | 'payment_system_id') => (e: SelectChangeEvent) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSwitchChange = (field: keyof TraderRate) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const activationBlocked = Boolean(
    !formData.is_active && !canActivate(rate?.trader_id, formData.payment_system_id),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rate ? 'Редактировать ставку' : 'Создать ставку'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Ставка"
              type="number"
              value={formData.rate}
              onChange={handleChange('rate')}
              inputProps={{ min: 0, step: 0.01 }}
              required
              fullWidth
            />
            <TextField
              label="Приоритет"
              type="number"
              value={formData.priority}
              onChange={handleChange('priority')}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Направление</InputLabel>
              <Select
                value={formData.direction}
                onChange={handleSelectChange('direction')}
                label="Направление"
              >
                <MenuItem value="IN">Входящий</MenuItem>
                <MenuItem value="OUT">Исходящий</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Дополнительная комиссия"
              type="number"
              value={formData.additional_commission}
              onChange={handleChange('additional_commission')}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Ref % TL"
              type="number"
              value={formData.referral_percent}
              onChange={handleChange('referral_percent')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Платежная система</InputLabel>
              <Select
                value={formData.payment_system_id}
                onChange={handleSelectChange('payment_system_id')}
                label="Платежная система"
                required
              >
                {paymentSystems.map((system) => (
                  <MenuItem key={system.id} value={system.id}>
                    {system.token}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleSwitchChange('is_active')}
                  disabled={activationBlocked}
                />
              }
              label="Активна"
            />
            {activationBlocked && (
              <Typography variant="caption" color="warning.main">
                {INSURANCE_BALANCE_ERROR}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface CreateRateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<TraderRate>) => Promise<void>;
  traders: Trader[];
  paymentSystems: PaymentSystem[];
  canActivate: (traderId?: string, paymentSystemId?: string) => boolean;
}

const CreateRateDialog: React.FC<CreateRateDialogProps> = ({ open, onClose, onSave, traders, paymentSystems, canActivate }) => {
  const [formData, setFormData] = useState<Partial<TraderRate>>({
    trader_id: '',
    payment_system_id: '',
    rate: 0,
    priority: 0,
    is_active: false,
    direction: 'IN',
    additional_commission: 0,
    referral_percent: 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
    onClose();
  };

  const handleChange = (field: keyof TraderRate) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: 'trader_id' | 'payment_system_id' | 'direction') => (e: SelectChangeEvent) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSwitchChange = (field: keyof TraderRate) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: Partial<TraderRate>) => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const activationBlocked = Boolean(
    !formData.is_active
      && !canActivate(formData.trader_id, formData.payment_system_id),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Создать ставку</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <SearchableSelect
              label="Трейдер"
              value={formData.trader_id}
              options={traders.map((trader) => ({ id: trader.id, label: trader.name }))}
              onChange={(value) => setFormData((prev) => ({ ...prev, trader_id: value }))}
              required
            />
            <SearchableSelect
              label="Платёжная система"
              value={formData.payment_system_id}
              options={paymentSystems.map((system) => ({ id: system.id, label: system.token }))}
              onChange={(value) => setFormData((prev) => ({ ...prev, payment_system_id: value }))}
              required
            />
            <TextField
              label="Ставка"
              type="number"
              value={formData.rate}
              onChange={handleChange('rate')}
              inputProps={{ min: 0, step: 0.01 }}
              required
              fullWidth
            />
            <TextField
              label="Приоритет"
              type="number"
              value={formData.priority}
              onChange={handleChange('priority')}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Направление</InputLabel>
              <Select
                value={formData.direction}
                onChange={handleSelectChange('direction')}
                label="Направление"
              >
                <MenuItem value="IN">Входящий</MenuItem>
                <MenuItem value="OUT">Исходящий</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Дополнительная комиссия"
              type="number"
              value={formData.additional_commission}
              onChange={handleChange('additional_commission')}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Ref % TL"
              type="number"
              value={formData.referral_percent}
              onChange={handleChange('referral_percent')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleSwitchChange('is_active')}
                  disabled={activationBlocked}
                />
              }
              label="Активна"
            />
            {activationBlocked && (
              <Typography variant="caption" color="warning.main">
                {INSURANCE_BALANCE_ERROR}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained">
            Создать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface RateAgentsDialogProps {
  open: boolean;
  rate: TraderRate | null;
  agents: User[];
  onClose: () => void;
}

const RateAgentsDialog: React.FC<RateAgentsDialogProps> = ({ open, rate, agents, onClose }) => {
  const { showNotification } = useNotification();
  const [items, setItems] = useState<TraderRateAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [agentUserId, setAgentUserId] = useState('');
  const [percent, setPercent] = useState('0');
  const [saving, setSaving] = useState(false);

  const loadAgents = async () => {
    if (!rate) return;
    setLoading(true);
    try {
      const data = await tradersService.getTraderRateAgents(rate.id);
      setItems(data);
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Ошибка при загрузке агентов ставки', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && rate) {
      setAgentUserId('');
      setPercent('0');
      loadAgents();
    }
  }, [open, rate?.id]);

  const handleCreate = async () => {
    if (!rate || !agentUserId) return;
    setSaving(true);
    try {
      await tradersService.createTraderRateAgent(rate.id, {
        agent_user_id: agentUserId,
        percent: Number(percent) || 0,
        is_active: true,
      });
      setAgentUserId('');
      setPercent('0');
      await loadAgents();
      showNotification('Агент добавлен к ставке', 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Ошибка при добавлении агента', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (agent: TraderRateAgent, patch: Partial<TraderRateAgent>) => {
    if (!rate) return;
    try {
      await tradersService.updateTraderRateAgent(rate.id, agent.id, patch);
      await loadAgents();
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Ошибка при обновлении агента', 'error');
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!rate) return;
    try {
      await tradersService.deleteTraderRateAgent(rate.id, agentId);
      await loadAgents();
      showNotification('Агент удален из ставки', 'success');
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Ошибка при удалении агента', 'error');
    }
  };

  const availableAgents = agents.filter(
    (agent) => !items.some((item) => item.agent_user_id === agent.id),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Агенты ставки {rate?.trader?.name || ''} {rate?.payment_system?.token ? `(${rate.payment_system.token})` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Агент</InputLabel>
              <Select
                value={agentUserId}
                label="Агент"
                onChange={(event) => setAgentUserId(event.target.value)}
              >
                {availableAgents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Ref %"
              type="number"
              value={percent}
              onChange={(event) => setPercent(event.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!agentUserId || saving}
              startIcon={<AddIcon />}
            >
              Добавить
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Агент</TableCell>
                    <TableCell>Ref %</TableCell>
                    <TableCell align="center">Активен</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((agent) => (
                    <TableRow key={agent.id}>
                      <TableCell>{agent.agent_username || agent.agent_user_id}</TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <TextField
                          size="small"
                          type="number"
                          defaultValue={agent.percent}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          onBlur={(event) =>
                            handleUpdate(agent, { percent: Number(event.target.value) || 0 })
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={agent.is_active}
                          onChange={(event) =>
                            handleUpdate(agent, { is_active: event.target.checked })
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(agent.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Агенты не назначены
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteRateDialogProps {
  open: boolean;
  rate: TraderRate | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteRateDialog: React.FC<DeleteRateDialogProps> = ({ open, rate, onClose, onConfirm }) => {
  const { showNotification } = useNotification();

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Error deleting rate:', err);
      showNotification('Ошибка при удалении ставки', 'error');
    }
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  if (!rate) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>Подтверждение удаления</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>Вы действительно хотите удалить ставку {rate.trader?.name} ({rate.payment_system?.token})?</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleConfirm} variant="contained" color="error">
          Удалить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TraderRatesRef {
  handleOpenDialog: () => void;
}

const TraderRates = forwardRef<TraderRatesRef>((props, ref) => {
  const [rates, setRates] = useState<TraderRate[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [balances, setBalances] = useState<TraderBalance[]>([]);
  const [agents, setAgents] = useState<User[]>([]);
  const [paymentSystems, setPaymentSystems] = useState<PaymentSystem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<TraderRate | null>(null);
  const [selectedPaymentSystem, setSelectedPaymentSystem] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'IN' | 'OUT'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { showNotification } = useNotification();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rateToDelete, setRateToDelete] = useState<TraderRate | null>(null);
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [rateForAgents, setRateForAgents] = useState<TraderRate | null>(null);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const canCreate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_RATES_CREATE), [currentUser]);
  const canToggleActive = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_RATES_TOGGLE_ACTIVE), [currentUser]);
  const canUpdate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_RATES_UPDATE), [currentUser]);
  const canDelete = useMemo(() => canPerformAction(currentUser, ActionKey.TRADER_RATES_DELETE), [currentUser]);

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      if (!canCreate) {
        showNotification('У вас нет прав для создания ставок', 'error');
        return;
      }
      setCreateDialogOpen(true);
    }
  }));

  const fetchRates = async () => {
    try {
      const ratesData = await tradersService.getTraderRates();
      setRates(ratesData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching rates:', err);
      setError(err.response?.data?.detail || 'Ошибка при загрузке ставок');
    } finally {
      setLoading(false);
    }
  };

  const fetchTraders = async () => {
    try {
      const data = await tradersService.getTraders({ is_archive: false });
      setTraders(data);
    } catch (err: any) {
      console.error('Error fetching traders:', err);
      showNotification(err.response?.data?.detail || 'Ошибка при загрузке трейдеров', 'error');
    }
  };

  const fetchPaymentSystems = async () => {
    try {
      const data = await dictionariesService.getPaymentSystems();
      setPaymentSystems(data);
    } catch (err: any) {
      console.error('Error fetching payment systems:', err);
      showNotification(err.response?.data?.detail || 'Ошибка при загрузке платежных систем', 'error');
    }
  };

  const fetchBalances = async () => {
    try {
      const data = await tradersService.getTraderBalances();
      setBalances(data);
    } catch (err: unknown) {
      console.error('Error fetching trader balances:', err);
      showNotification(getErrorMessage(err, 'Ошибка при загрузке балансов трейдеров'), 'error');
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await usersService.getUsers();
      setAgents(
        data.filter(
          (user: User) => user.role === RoleEnum.TRADERS_OWNER && user.is_active,
        ),
      );
    } catch (err: any) {
      console.error('Error fetching agents:', err);
      showNotification(err.response?.data?.detail || 'Ошибка при загрузке агентов', 'error');
    }
  };

  const handleCreateRate = async (data: Partial<TraderRate>) => {
    if (!canCreate) {
      showNotification('У вас нет прав для создания ставок', 'error');
      return;
    }
    try {
      await tradersService.createTraderRate(data);
      await fetchRates();
      showNotification('Ставка успешно создана', 'success');
    } catch (err: unknown) {
      console.error('Error creating rate:', err);
      showNotification(getErrorMessage(err, 'Ошибка при создании ставки'), 'error');
    }
  };

  const handleEditRate = async (rateId: string, data: Partial<TraderRate>) => {
    if (!canUpdate) {
      showNotification('У вас нет прав для редактирования ставок', 'error');
      return;
    }
    try {
      await tradersService.updateTraderRate(rateId, data);
      await fetchRates();
      showNotification('Ставка успешно обновлена', 'success');
    } catch (err: unknown) {
      console.error('Error updating rate:', err);
      showNotification(getErrorMessage(err, 'Ошибка при обновлении ставки'), 'error');
    }
  };

  const handleDeleteRate = async (rateId: string) => {
    if (!canDelete) {
      showNotification('У вас нет прав для удаления ставок', 'error');
      return;
    }
    try {
      await tradersService.deleteTraderRate(rateId);
      await fetchRates();
      showNotification('Ставка успешно удалена', 'success');
    } catch (err: any) {
      console.error('Error deleting rate:', err);
      showNotification('Ошибка при удалении ставки', 'error');
    }
  };

  const handleDeleteClick = (rate: TraderRate) => {
    setRateToDelete(rate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (rateToDelete) {
      await handleDeleteRate(rateToDelete.id);
    }
  };

  const sortedPaymentSystems = useMemo(
    () =>
      [...paymentSystems].sort((a, b) =>
        a.token.localeCompare(b.token, 'en'),
      ),
    [paymentSystems],
  );

  const canActivateRate = (traderId?: string, paymentSystemId?: string): boolean => {
    if (!traderId || !paymentSystemId) return false;

    const paymentSystem = paymentSystems.find((item) => item.id === paymentSystemId);
    const currencyId = paymentSystem?.currency_id
      || (typeof paymentSystem?.currency === 'string'
        ? paymentSystem.currency
        : paymentSystem?.currency?.id);
    if (!currencyId) return false;

    return balances.some(
      (balance) => balance.trader_id === traderId
        && balance.currency_id === currencyId
        && Number(balance.insurance_balance_usdt) > 0,
    );
  };

  const filteredRates = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    let result = [...rates];
    if (selectedPaymentSystem !== 'all') {
      result = result.filter(rate => rate.payment_system_id === selectedPaymentSystem);
    }
    result = result.filter(rate => (!needle || [rate.trader?.name, rate.trader_id, rate.payment_system?.token, rate.id].some(value => value?.toLowerCase().includes(needle))) && (directionFilter === 'all' || rate.direction === directionFilter) && (activeFilter === 'all' || rate.is_active === (activeFilter === 'active')));
    return result.sort((a, b) => {
      if (sortDirection === 'asc') {
        return a.priority - b.priority;
      }
      return b.priority - a.priority;
    });
  }, [rates, selectedPaymentSystem, searchQuery, directionFilter, activeFilter, sortDirection]);

  useEffect(() => {
    Promise.all([
      fetchRates(),
      fetchTraders(),
      fetchPaymentSystems(),
      fetchBalances(),
      fetchAgents(),
    ]);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{typeof error === 'string' ? error : 'Произошла ошибка'}</Alert>
      </Box>
    );
  }

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Ставки трейдеров</Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <TextField sx={{ flex: 1, minWidth: 280 }} label="Поиск по трейдеру, методу или ID" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
            <FormControl sx={{ minWidth: 230 }}><InputLabel>Платежная система</InputLabel><Select value={selectedPaymentSystem} label="Платежная система" onChange={(event) => setSelectedPaymentSystem(event.target.value)}><MenuItem value="all">Все</MenuItem>{sortedPaymentSystems.map((system) => <MenuItem key={system.id} value={system.id}>{system.token}</MenuItem>)}</Select></FormControl>
            <FormControl sx={{ minWidth: 170 }}><InputLabel>Направление</InputLabel><Select value={directionFilter} label="Направление" onChange={(event) => setDirectionFilter(event.target.value as typeof directionFilter)}><MenuItem value="all">Все</MenuItem><MenuItem value="IN">IN</MenuItem><MenuItem value="OUT">OUT</MenuItem></Select></FormControl>
            <FormControl sx={{ minWidth: 170 }}><InputLabel>Активность</InputLabel><Select value={activeFilter} label="Активность" onChange={(event) => setActiveFilter(event.target.value as typeof activeFilter)}><MenuItem value="all">Все</MenuItem><MenuItem value="active">Активные</MenuItem><MenuItem value="inactive">Неактивные</MenuItem></Select></FormControl>
            <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => { setSearchQuery(''); setSelectedPaymentSystem('all'); setDirectionFilter('all'); setActiveFilter('all'); }}>Сбросить</Button>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Трейдер</TableCell>
                  <TableCell>Платежная система</TableCell>
                  <TableCell>Ставка</TableCell>
                  <TableCell>Приоритет</TableCell>
                  <TableCell>Направление</TableCell>
                  <TableCell>Доп. комиссия</TableCell>
                  <TableCell>Ref % TL</TableCell>
                  <TableCell>Агенты</TableCell>
                  <TableCell align="center">Активна</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell>{rate.trader?.name || '-'}</TableCell>
                    <TableCell>{rate.payment_system?.token || '-'}</TableCell>
                    <TableCell>{rate.rate}</TableCell>
                    <TableCell>{rate.priority}</TableCell>
                    <TableCell>{rate.direction === 'IN' ? 'Входящий' : 'Исходящий'}</TableCell>
                    <TableCell>{rate.additional_commission}</TableCell>
                    <TableCell>{rate.referral_percent}</TableCell>
                    <TableCell>
                      <Tooltip title={canUpdate ? 'Настроить агентов' : 'Недостаточно прав'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (!canUpdate) {
                                showNotification('У вас нет прав для редактирования агентов ставки', 'error');
                                return;
                              }
                              setRateForAgents(rate);
                              setAgentsDialogOpen(true);
                            }}
                            disabled={!canUpdate}
                          >
                            <PeopleIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          !canToggleActive
                            ? 'Недостаточно прав'
                            : !rate.is_active && !canActivateRate(rate.trader_id, rate.payment_system_id)
                              ? INSURANCE_BALANCE_ERROR
                              : 'Изменить статус'
                        }
                      >
                        <span>
                          <Switch
                            checked={rate.is_active}
                            onChange={async (e) => {
                              if (!canToggleActive) {
                                showNotification('У вас нет прав для выполнения этой операции', 'error');
                                return;
                              }
                              try {
                                const updatedRate = await tradersService.toggleTraderRateActive(rate.id);
                                setRates((prev) =>
                                  prev.map((item) =>
                                    item.id === rate.id
                                      ? {
                                          ...item,
                                          is_active: updatedRate?.is_active ?? !item.is_active,
                                        }
                                      : item,
                                  ),
                                );
                                showNotification(
                                  `Ставка успешно ${updatedRate?.is_active ? 'активирована' : 'деактивирована'}`,
                                  'success'
                                );
                              } catch (err: unknown) {
                                console.error('Error toggling rate status:', err);
                                showNotification(
                                  getErrorMessage(err, 'Ошибка при обновлении статуса ставки'),
                                  'error',
                                );
                              }
                            }}
                            disabled={
                              !canToggleActive
                              || (!rate.is_active && !canActivateRate(rate.trader_id, rate.payment_system_id))
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title={canUpdate ? 'Редактировать' : 'Недостаточно прав'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!canUpdate) {
                                  showNotification('У вас нет прав для редактирования ставок', 'error');
                                  return;
                                }
                                setSelectedRate(rate);
                                setEditDialogOpen(true);
                              }}
                              sx={{ color: '#1976d2' }}
                              disabled={!canUpdate}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={canDelete ? 'Удалить' : 'Недостаточно прав'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(rate)}
                              sx={{ color: '#d32f2f' }}
                              disabled={!canDelete}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CreateRateDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleCreateRate}
          traders={traders}
          paymentSystems={paymentSystems}
          canActivate={canActivateRate}
        />

        <EditRateDialog
          open={editDialogOpen}
          rate={selectedRate}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedRate(null);
          }}
          onSave={handleEditRate}
          paymentSystems={paymentSystems}
          canActivate={canActivateRate}
        />

        <RateAgentsDialog
          open={agentsDialogOpen}
          rate={rateForAgents}
          agents={agents}
          onClose={() => {
            setAgentsDialogOpen(false);
            setRateForAgents(null);
          }}
        />

        <DeleteRateDialog
          open={deleteDialogOpen}
          rate={rateToDelete}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
        />
      </Box>
    </AnimatedPage>
  );
});

export default TraderRates; 
